import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BatteryCharging,
  Camera,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  ExternalLink,
  Fuel,
  Gauge,
  Globe2,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  Navigation,
  ReceiptText,
  Route,
  ShieldCheck,
  Smartphone,
  Wallet,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import {
  ASSETS,
  AUDIENCES,
  BENEFITS,
  CONTACTS,
  FAQ_ITEMS,
  FEATURES,
  FREE_POINTS,
  HERO_BADGES,
  NAV_ITEMS,
  PREMIUM_POINTS,
  PRIVACY_POINTS,
  STORE_LINKS,
} from "@/data/eTanikaContent";

const iconMap = {
  map: MapPin,
  receipt: ReceiptText,
  garage: Car,
  zap: Zap,
  camera: Camera,
  fuel: Fuel,
  battery: BatteryCharging,
  navigation: Navigation,
  car: Car,
  wallet: Wallet,
  route: Route,
};

const openLinkProps = {
  target: "_blank",
  rel: "noopener noreferrer",
};

function StoreButtons({ prefix, compact = false, align = "left", className = "" }) {
  const size = compact ? "sm" : "lg";
  const wrapperAlign = align === "center" ? "justify-center" : "justify-start";

  return (
    <div className={`store-buttons ${wrapperAlign} ${className}`} data-testid={`${prefix}-store-buttons`}>
      <Button asChild size={size} className="store-button store-button-primary">
        <a
          href={STORE_LINKS.apple}
          data-testid={`${prefix}-app-store-button`}
          aria-label="Scarica e-Tanika su App Store"
          {...openLinkProps}
        >
          <Smartphone aria-hidden="true" />
          <span>{compact ? "App Store" : "Scarica su App Store"}</span>
          <ExternalLink aria-hidden="true" className="external-icon" />
        </a>
      </Button>
      <Button asChild size={size} variant="secondary" className="store-button store-button-secondary">
        <a
          href={STORE_LINKS.google}
          data-testid={`${prefix}-google-play-button`}
          aria-label="Scarica e-Tanika su Google Play"
          {...openLinkProps}
        >
          <Download aria-hidden="true" />
          <span>{compact ? "Google Play" : "Disponibile su Google Play"}</span>
          <ExternalLink aria-hidden="true" className="external-icon" />
        </a>
      </Button>
    </div>
  );
}

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`site-header ${isScrolled ? "site-header-scrolled" : ""}`} data-testid="site-header">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <a href="#hero" className="brand-lockup" data-testid="brand-home-link" aria-label="Vai all'inizio della landing e-Tanika">
          <img src={ASSETS.icon} alt="Icona app e-Tanika" className="brand-icon" />
          <span>
            <strong>e-Tanika</strong>
            <small>Mobilità, costi e rifornimenti</small>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigazione principale">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="nav-link"
              data-testid={`nav-${item.id}-link`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <StoreButtons prefix="header" compact />
        </div>

        <button
          type="button"
          className="mobile-menu-button lg:hidden"
          onClick={() => setIsMenuOpen((value) => !value)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          data-testid="mobile-menu-button"
        >
          {isMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          <span className="sr-only">Apri menu</span>
        </button>
      </div>

      {isMenuOpen && (
        <div id="mobile-menu" className="mobile-menu lg:hidden" data-testid="mobile-menu-panel">
          <nav className="grid gap-1" aria-label="Navigazione mobile">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="mobile-nav-link"
                onClick={closeMenu}
                data-testid={`mobile-nav-${item.id}-link`}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <StoreButtons prefix="mobile-menu" compact className="mt-4" />
        </div>
      )}
    </header>
  );
}

function SectionIntro({ kicker, title, children, align = "left" }) {
  return (
    <div className={`section-intro ${align === "center" ? "section-intro-center" : ""}`}>
      <p className="section-kicker">{kicker}</p>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function Hero() {
  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <div className="map-pattern" aria-hidden="true" />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.04fr_0.96fr] lg:py-24">
        <div className="hero-copy">
          <Badge variant="secondary" className="hero-badge" data-testid="hero-source-badge">
            App gratuita per rifornimenti, ricariche e costi veicolo
          </Badge>
          <h1>Trova dove conviene fare rifornimento. Tieni sotto controllo ogni costo del tuo mezzo.</h1>
          <p className="hero-lead">
            e-Tanika riunisce mappa distributori e colonnine, confronto prezzi, garage digitale, rifornimenti, consumi e spese in un’unica app pensata per auto ibride, elettriche e a combustione tradizionale.
          </p>
          <div className="hero-badges" aria-label="Punti chiave e-Tanika">
            {HERO_BADGES.map((badge) => (
              <span key={badge} data-testid={`hero-badge-${badge.toLowerCase().replaceAll(" ", "-")}`}>
                <Check aria-hidden="true" />
                <span>{badge}</span>
              </span>
            ))}
          </div>
          <div className="hero-actions">
            <StoreButtons prefix="hero" />
            <div className="hero-secondary-actions">
              <Button asChild variant="outline" className="ghost-action" data-testid="hero-download-button">
                <a href="#download">Scarica l’app</a>
              </Button>
              <Button asChild variant="ghost" className="ghost-action" data-testid="hero-features-button">
                <a href="#funzionalita">Scopri le funzionalità</a>
              </Button>
            </div>
          </div>
        </div>

        <div className="hero-visual" aria-label="Anteprima screenshot e-Tanika">
          <div className="hero-device-card">
            <div className="device-topline">
              <img src={ASSETS.icon} alt="Icona e-Tanika" />
              <div>
                <strong>e-Tanika</strong>
                <span>Mappa, garage, storico</span>
              </div>
            </div>
            <img
              src={ASSETS.screenshots[0].src}
              alt="Anteprima mappa distributori e-Tanika"
              className="hero-main-shot"
              loading="eager"
              decoding="async"
              data-testid="hero-screenshot"
            />
            <div className="hero-mini-grid">
              <div>
                <Gauge aria-hidden="true" />
                <span>Prezzi carburante</span>
              </div>
              <div>
                <ShieldCheck aria-hidden="true" />
                <span>Dati locali</span>
              </div>
            </div>
          </div>
          <Card className="floating-card floating-card-left" data-testid="hero-floating-card-eco">
            <CardContent className="p-4">
              <Navigation aria-hidden="true" />
              <strong>Eco-Scelta</strong>
              <span>Vai al distributore più conveniente.</span>
            </CardContent>
          </Card>
          <Card className="floating-card floating-card-right" data-testid="hero-floating-card-premium">
            <CardContent className="p-4">
              <CreditCard aria-hidden="true" />
              <strong>Premium</strong>
              <span>Una tantum, senza abbonamenti.</span>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section id="vantaggi" className="section-block" data-testid="benefits-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionIntro kicker="Perché usarla" title="Meno dispersione, più controllo sulla mobilità quotidiana">
          e-Tanika ti aiuta a spendere meglio, registrare tutto più velocemente e avere sott’occhio i costi reali di utilizzo dei tuoi mezzi.
        </SectionIntro>
        <div className="bento-grid mt-10">
          {BENEFITS.map((benefit, index) => {
            const Icon = iconMap[benefit.icon] || Check;
            return (
              <Card
                key={benefit.title}
                className={`benefit-card ${index === 0 ? "benefit-card-large" : ""}`}
                data-testid={`benefit-card-${index}`}
              >
                <CardContent className="p-6">
                  <span className="icon-chip"><Icon aria-hidden="true" /></span>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="funzionalita" className="section-block section-wash" data-testid="features-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionIntro kicker="Funzionalità" title="Tutto ciò che serve per gestire carburante, ricariche e mezzi" align="center">
          Una panoramica concreta delle funzioni principali: mappa, prezzi, rifornimenti, garage digitale, spese e strumenti Premium.
        </SectionIntro>
        <div className="features-grid mt-12">
          {FEATURES.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Check;
            return (
              <Card key={feature.title} className="feature-card" data-testid={`feature-card-${index}`}>
                <CardContent className="p-6">
                  <div className="feature-card-header">
                    <span className="icon-chip"><Icon aria-hidden="true" /></span>
                    <Badge variant="outline">{index < 3 ? "Mappa" : index < 5 ? "Gestione" : "Premium"}</Badge>
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                  <ul>
                    {feature.points.map((point) => (
                      <li key={point}><Check aria-hidden="true" /> {point}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ScreenshotGallery() {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const selectedShot = selectedIndex !== null ? ASSETS.screenshots[selectedIndex] : null;

  const goTo = (direction) => {
    setSelectedIndex((current) => {
      if (current === null) return 0;
      const next = current + direction;
      if (next < 0) return ASSETS.screenshots.length - 1;
      if (next >= ASSETS.screenshots.length) return 0;
      return next;
    });
  };

  return (
    <section id="screenshot" className="section-block" data-testid="screenshots-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionIntro kicker="Screenshot reali" title="Guarda l’app prima di scaricarla">
          Guarda gli screenshot dell’app e scopri come e-Tanika organizza mappa, garage, rifornimenti e costi.
        </SectionIntro>
        <div className="screenshots-gallery mt-10" data-testid="screenshots-gallery">
          {ASSETS.screenshots.map((shot, index) => (
            <button
              key={shot.src}
              type="button"
              className="screenshot-tile"
              onClick={() => setSelectedIndex(index)}
              data-testid={`screenshot-open-button-${index}`}
              aria-label={`Apri ${shot.alt}`}
            >
              <img src={shot.src} alt={shot.alt} loading={index < 2 ? "eager" : "lazy"} decoding="async" />
              <span>Apri screenshot</span>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent className="screenshot-dialog" data-testid="screenshot-lightbox-dialog">
          <DialogHeader>
            <DialogTitle>Screenshot e-Tanika</DialogTitle>
            <DialogDescription>
              Screenshot {selectedIndex !== null ? selectedIndex + 1 : 1} di {ASSETS.screenshots.length} dell’app e-Tanika.
            </DialogDescription>
          </DialogHeader>
          {selectedShot && (
            <div className="lightbox-stage">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="lightbox-nav lightbox-prev"
                onClick={() => goTo(-1)}
                data-testid="screenshot-prev-button"
                aria-label="Screenshot precedente"
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <img src={selectedShot.src} alt={selectedShot.alt} data-testid="screenshot-lightbox-image" />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="lightbox-nav lightbox-next"
                onClick={() => goTo(1)}
                data-testid="screenshot-next-button"
                aria-label="Screenshot successivo"
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Privacy() {
  return (
    <section id="privacy" className="section-block privacy-section" data-testid="privacy-section">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.92fr_1.08fr]">
        <div>
          <SectionIntro kicker="Privacy e dati" title="Local-first, senza account obbligatorio">
            e-Tanika sceglie un approccio pratico: nessuna registrazione richiesta e dati salvati localmente sul dispositivo. La scansione foto è l’unico caso in cui l’immagine viene inviata temporaneamente ai servizi necessari per il riconoscimento.
          </SectionIntro>
          <div className="privacy-actions">
            <Button asChild variant="outline" data-testid="privacy-policy-link">
              <a href={CONTACTS.privacyPolicy} {...openLinkProps}>
                Privacy policy <ExternalLink aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
        <Card className="privacy-card">
          <CardContent className="p-6 sm:p-8">
            <div className="privacy-card-title">
              <ShieldCheck aria-hidden="true" />
              <h3>Privacy in pratica</h3>
            </div>
            <ul className="check-list">
              {PRIVACY_POINTS.map((point) => (
                <li key={point} data-testid="privacy-point"><Check aria-hidden="true" /> {point}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Premium() {
  return (
    <section id="premium" className="section-block" data-testid="premium-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionIntro kicker="Gratis subito, Premium per sempre" title="Funzioni avanzate senza abbonamento" align="center">
          Usa e-Tanika subito gratuitamente. Con Premium sblocchi funzioni avanzate con pagamento una tantum: niente abbonamenti. Su iOS il prezzo è 7,99 €.
        </SectionIntro>
        <div className="pricing-grid mt-10">
          <Card className="pricing-card" data-testid="free-card">
            <CardContent className="p-6 sm:p-8">
              <Badge variant="secondary">Gratis</Badge>
              <h3>Parti subito</h3>
              <p>Le funzioni essenziali per mappa, confronto prezzi e controllo dei costi.</p>
              <ul className="check-list">
                {FREE_POINTS.map((point) => <li key={point}><Check aria-hidden="true" /> {point}</li>)}
              </ul>
            </CardContent>
          </Card>
          <Card className="pricing-card pricing-card-premium" data-testid="premium-card">
            <CardContent className="p-6 sm:p-8">
              <Badge>Premium una tantum</Badge>
              <h3>Strumenti avanzati</h3>
              <p>Per velocizzare la gestione e pianificare meglio spostamenti, rifornimenti e costi.</p>
              <ul className="check-list">
                {PREMIUM_POINTS.map((point) => <li key={point}><Check aria-hidden="true" /> {point}</li>)}
              </ul>
              <button
                type="button"
                className="premium-preview"
                onClick={() => document.querySelector('[data-testid="screenshot-open-button-9"]')?.click()}
                data-testid="premium-preview-button"
                aria-label="Apri screenshot Premium nella gallery"
              >
                <img
                  src={ASSETS.premiumScreenshot}
                  alt="Anteprima e-Tanika Premium con funzionalità avanzate"
                  loading="lazy"
                  decoding="async"
                />
                <span>Anteprima Premium</span>
              </button>
              <StoreButtons prefix="premium-cta" compact className="mt-6" />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Audience() {
  return (
    <section id="per-chi" className="section-block section-wash" data-testid="audience-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionIntro kicker="Per chi è utile" title="Un compagno quotidiano per chi guida, lavora e tiene ai costi">
          e-Tanika è pensata per chi vuole uno strumento semplice, rapido e ordinato per gestire mezzi e spese.
        </SectionIntro>
        <div className="audience-grid mt-10">
          {AUDIENCES.map((audience, index) => (
            <Card key={audience} className="audience-card" data-testid={`audience-card-${index}`}>
              <CardContent className="p-5">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{audience}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section id="faq" className="section-block" data-testid="faq-section">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.82fr_1.18fr]">
        <SectionIntro kicker="Domande frequenti" title="Le risposte principali prima del download">
          Risposte chiare alle domande più comuni prima di scaricare l’app.
        </SectionIntro>
        <Accordion type="single" collapsible className="faq-accordion" data-testid="faq-accordion">
          {FAQ_ITEMS.map((item, index) => (
            <AccordionItem value={`item-${index}`} key={item.q}>
              <AccordionTrigger data-testid={`faq-trigger-${index}`}>{item.q}</AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${index}`}>{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function DownloadCTA() {
  return (
    <section id="download" className="download-section" data-testid="download-section">
      <div className="mx-auto max-w-5xl px-4 py-14 text-center sm:px-6 sm:py-20">
        <Badge variant="secondary">Disponibile su iOS e Android</Badge>
        <h2>Scarica e-Tanika e porta ordine nella gestione dei tuoi mezzi.</h2>
        <p>
          Trova distributori e colonnine, confronta prezzi, registra rifornimenti e tieni sotto controllo consumi e spese.
        </p>
        <StoreButtons prefix="download" align="center" className="mt-8" />
        <p className="download-microcopy">Senza account obbligatorio. I tuoi dati restano salvati localmente sul dispositivo.</p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="footer">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <a href="#hero" className="brand-lockup footer-brand" data-testid="footer-brand-link">
            <img src={ASSETS.icon} alt="Icona e-Tanika" className="brand-icon" />
            <span>
              <strong>e-Tanika</strong>
              <small>Gestione veicoli semplice e ordinata</small>
            </span>
          </a>
          <p className="footer-copy">
            Trova carburante e colonnine, registra rifornimenti e tieni sotto controllo consumi, spese e garage digitale.
          </p>
        </div>
        <div className="footer-links">
          <a href={CONTACTS.website} data-testid="footer-website-link" {...openLinkProps}><Globe2 aria-hidden="true" /> Sito sviluppatore</a>
          <a href={`mailto:${CONTACTS.supportEmail}`} data-testid="footer-support-email-link"><Mail aria-hidden="true" /> {CONTACTS.supportEmail}</a>
          <a href={`mailto:${CONTACTS.appStoreSupportEmail}`} data-testid="footer-appstore-email-link"><Mail aria-hidden="true" /> {CONTACTS.appStoreSupportEmail}</a>
          <a href={CONTACTS.privacyPolicy} data-testid="footer-privacy-link" {...openLinkProps}><LockKeyhole aria-hidden="true" /> Privacy policy</a>
          <a href={STORE_LINKS.apple} data-testid="footer-app-store-link" {...openLinkProps}>App Store <ExternalLink aria-hidden="true" /></a>
          <a href={STORE_LINKS.google} data-testid="footer-google-play-link" {...openLinkProps}>Google Play <ExternalLink aria-hidden="true" /></a>
        </div>
      </div>
    </footer>
  );
}

function MobileDownloadBar() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const download = document.getElementById("download");
      const downloadTop = download ? download.getBoundingClientRect().top : Infinity;
      setVisible(window.scrollY > 420 && downloadTop > 260);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`mobile-download-bar ${visible ? "mobile-download-bar-visible" : ""}`} data-testid="mobile-download-bar" aria-hidden={!visible}>
      <div>
        <strong>Scarica e-Tanika</strong>
        <span>Gratis subito</span>
      </div>
      <StoreButtons prefix="mobile-download-bar" compact />
    </div>
  );
}

function App() {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="App landing-shell">
      <Header />
      <main>
        <Hero />
        <Benefits />
        <Features />
        <ScreenshotGallery />
        <Privacy />
        <Premium />
        <Audience />
        <FAQ />
        <DownloadCTA />
      </main>
      <Footer />
      <MobileDownloadBar />
      <div className="sr-only" aria-live="polite">© {currentYear} {CONTACTS.seller}</div>
    </div>
  );
}

export default App;
